import { nextTick, ref, watch, type Ref } from "vue";
import { readCommentStore } from "../editor/commentStore";
import type { CommentSurface } from "../editor/commentSurface";
import type { ThreadPlacement } from "../components/CommentMargin.vue";
import { isJust, isNothing, toMaybe, type Maybe } from "../lib/maybe";

/** Comment cards always use this width. */
const COMMENT_CARD_WIDTH = 288;
/** Collapsed dimmed previews can shrink down to this width. */
const COLLAPSED_MIN_WIDTH = 200;
/** Preferred right gutter when comments are present. */
const COMMENT_GUTTER_PX = 300;
/** Prose column max width in rem (matches the .zw-prose max-width: 40rem). */
const PROSE_MAX_REM = 40;
/** Horizontal inset used when placing margin cards inside the shell. */
const COMMENT_MARGIN_PAD = 12;

/** Prose column centering/gutter math and comment-card placement. Reads
 * `openIds` from the comment-threads composable (created before this one)
 * to decide which cards are expanded vs. collapsed-hidden. */
export function useEditorLayout(options: {
  getSurface: () => Maybe<CommentSurface>;
  /** The active document text (`.zwdoc`, or briefly legacy Markdown during conversion). */
  doc: Ref<string>;
  shell: Ref<Maybe<HTMLElement>>;
  mobileShell: Ref<boolean>;
  uiZoomPercent: Ref<number>;
  outlineCollapsed: Ref<boolean>;
  /** Manuscript thesaurus / DB context rail. Width changes recenter prose. */
  contextPanelCollapsed: Ref<boolean>;
  openIds: Ref<Set<string>>;
  /** When false (Focus / public Reading), do not reserve a comment gutter. */
  commentsEnabled: Ref<boolean>;
}): {
  proseMotionReady: Ref<boolean>;
  contentReady: Ref<boolean>;
  layoutInstant: Ref<boolean>;
  placements: Ref<ThreadPlacement[]>;
  cardHeights: Ref<Record<string, number>>;
  syncProseLayout: () => void;
  measureCardHeights: () => void;
  recomputePlacements: () => void;
  scheduleLayout: () => void;
  snapLayout: () => void;
  cleanup: () => void;
} {
  /** Enable prose margin easing only after the first settled layout. */
  const proseMotionReady = ref(false);
  /** Hide the manuscript surface until its first layout pass has centered the
   * prose column — otherwise a fresh mount briefly flashes flush-left content
   * before `syncProseLayout` writes `--zw-prose-margin-left`. Revealed instantly
   * (no fade) once that margin is set, with motion still disabled. */
  const contentReady = ref(false);
  /** Skip comment card position easing while the outline snaps open/closed. */
  const layoutInstant = ref(false);

  const placements = ref<ThreadPlacement[]>([]);
  const cardHeights = ref<Record<string, number>>({});

  let layoutRaf = 0;

  function rootFontPx(): number {
    const parsed = Number.parseFloat(
      getComputedStyle(document.documentElement).fontSize,
    );
    return Number.isFinite(parsed) ? parsed : 16;
  }

  /** Live px width of the prose column — must track UI zoom (rem root). */
  function proseMaxPx(): number {
    return PROSE_MAX_REM * rootFontPx();
  }

  /** Usable width for margin comments after prose + placement padding. */
  function commentGutterBudget(shellWidth: number): number {
    return Math.min(
      COMMENT_GUTTER_PX,
      shellWidth - proseMaxPx() - COMMENT_MARGIN_PAD * 2,
    );
  }

  function shouldReserveCommentGutter(
    shellWidth: number,
    hasComments: boolean,
  ): boolean {
    return (
      hasComments && commentGutterBudget(shellWidth) >= COLLAPSED_MIN_WIDTH
    );
  }

  /**
   * With a comment gutter: keep prose under the ribbon (viewport center),
   * shifting left only if needed for the reserved margin.
   * Without a gutter (shell can't fit prose + collapsed comments / no comments):
   * center in the editor shell so the column doesn't sit off to the right.
   *
   * Always writes a px length (never `auto`) so margin-left can ease between
   * centered and gutter-shifted layouts when the first comment appears.
   */
  function syncProseLayout(): void {
    const surfaceM = options.getSurface();
    const shellM = options.shell.value;
    if (isNothing(shellM) || isNothing(surfaceM)) return;
    const shellRect = shellM.value.getBoundingClientRect();
    const hasComments =
      options.commentsEnabled.value === true &&
      surfaceM.value.anchorPositions().length > 0;
    const proseMax = proseMaxPx();
    const proseWidth = Math.min(proseMax, shellRect.width);
    const shellCenter = Math.max(0, (shellRect.width - proseWidth) / 2);

    let marginLeft = shellCenter;
    if (shouldReserveCommentGutter(shellRect.width, hasComments)) {
      const gutterReserve =
        commentGutterBudget(shellRect.width) + COMMENT_MARGIN_PAD * 2;
      const viewportCenter =
        window.innerWidth / 2 - proseMax / 2 - shellRect.left;
      const gutterConstrained = shellRect.width - proseWidth - gutterReserve;
      marginLeft = Math.max(0, Math.min(viewportCenter, gutterConstrained));
    }

    const next = `${Math.round(marginLeft)}px`;
    if (
      shellM.value.style.getPropertyValue("--zw-prose-margin-left") === next
    ) {
      return;
    }
    shellM.value.style.setProperty("--zw-prose-margin-left", next);
  }

  function measureCardHeights(): void {
    const shellM = options.shell.value;
    if (isNothing(shellM)) return;
    const next: Record<string, number> = {};
    for (const el of shellM.value.querySelectorAll("[data-margin-comment]")) {
      if (el instanceof HTMLElement) {
        const id = toMaybe(el.dataset.marginComment);
        if (isJust(id) && id.value.length > 0) {
          next[id.value] = el.offsetHeight;
        }
      }
    }
    cardHeights.value = next;
  }

  function recomputePlacements(): void {
    const surfaceM = options.getSurface();
    const shellM = options.shell.value;
    if (isNothing(surfaceM) || isNothing(shellM)) {
      placements.value = [];
      return;
    }
    const surface = surfaceM.value;

    const shellRect = shellM.value.getBoundingClientRect();
    const contentRect = surface.contentRect();
    const anchors =
      options.commentsEnabled.value === true ? surface.anchorPositions() : [];
    const store = readCommentStore(options.doc.value);

    const gap = 10;
    const marginPad = COMMENT_MARGIN_PAD;
    // Keep hide/show in lockstep with prose recentering (same budget check).
    const hideCollapsed =
      shouldReserveCommentGutter(shellRect.width, anchors.length > 0) === false;
    const collapsedWidth = Math.min(
      COMMENT_CARD_WIDTH,
      Math.max(COLLAPSED_MIN_WIDTH, commentGutterBudget(shellRect.width)),
    );

    type Raw = {
      id: string;
      naturalTop: number;
      estimate: number;
      width: number;
      left: number;
    };
    const raw: Raw[] = [];
    for (const anchor of anchors) {
      const thread = toMaybe(store[anchor.id]);
      if (isNothing(thread)) continue;
      if (
        hideCollapsed === true &&
        options.openIds.value.has(anchor.id) === false
      ) {
        continue;
      }
      const coords = surface.coordsAtPos(anchor.pos);
      if (coords === null) continue;
      const naturalTop = coords.top - shellRect.top;
      const open = options.openIds.value.has(anchor.id);
      const resolved = thread.value.resolved;
      const width = open === true ? COMMENT_CARD_WIDTH : collapsedWidth;
      // Prefer sitting just right of the prose column; clamp into the shell.
      let left = contentRect.right - shellRect.left + marginPad;
      if (left + width > shellRect.width - marginPad) {
        left = Math.max(marginPad, shellRect.width - width - marginPad);
      }
      const estimate =
        cardHeights.value[anchor.id] ??
        (open === true
          ? resolved === true
            ? 72
            : 48 + thread.value.messages.length * 72
          : 44);
      raw.push({ id: anchor.id, naturalTop, estimate, width, left });
    }

    raw.sort((a, b) => a.naturalTop - b.naturalTop);

    const next: ThreadPlacement[] = [];
    let cursor = -Infinity;
    for (const item of raw) {
      let top = item.naturalTop;
      if (top < cursor + gap) top = cursor + gap;
      next.push({
        id: item.id,
        top,
        left: item.left,
        width: item.width,
      });
      cursor = top + item.estimate;
    }

    placements.value = next;
  }

  function scheduleLayout(): void {
    if (layoutRaf !== 0) cancelAnimationFrame(layoutRaf);
    layoutRaf = requestAnimationFrame(() => {
      layoutRaf = 0;
      syncProseLayout();
      // drawSelection() layers are measured coords. When --zw-prose-margin-left
      // shifts the content without a doc transaction, remeasure so the amber
      // highlight stays under the glyphs.
      const surfaceM = options.getSurface();
      if (isJust(surfaceM)) surfaceM.value.requestMeasure();
      recomputePlacements();
      void nextTick(() => {
        measureCardHeights();
        recomputePlacements();
        const surfaceAfter = options.getSurface();
        if (isJust(surfaceAfter)) surfaceAfter.value.requestMeasure();
      });
    });
  }

  /** Snap layout (no easing) around a discrete shell-width change. */
  function snapLayout(): void {
    const restoreMotion = proseMotionReady.value;
    proseMotionReady.value = false;
    layoutInstant.value = true;
    scheduleLayout();
    void nextTick(() => {
      scheduleLayout();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          proseMotionReady.value = restoreMotion;
          layoutInstant.value = false;
        });
      });
    });
  }

  watch(options.uiZoomPercent, () => {
    // Snap layout — rem-based prose width changes with zoom; don't ease mid-shift.
    snapLayout();
  });

  watch(options.outlineCollapsed, () => {
    if (options.mobileShell.value) return;
    // Outline width snaps; keep the editor (and comment cards) from easing.
    snapLayout();
  });

  watch(options.contextPanelCollapsed, () => {
    if (options.mobileShell.value) return;
    snapLayout();
  });

  watch(options.commentsEnabled, () => {
    snapLayout();
  });

  // The desktop and narrow layouts swap the outline, context rail and comment
  // layer without a window resize event of their own (the flip lands after the
  // width debounce), so re-measure once the new layout is in the DOM (ZW-276).
  watch(
    options.mobileShell,
    () => {
      snapLayout();
    },
    { flush: "post" },
  );

  function cleanup(): void {
    if (layoutRaf !== 0) cancelAnimationFrame(layoutRaf);
  }

  return {
    proseMotionReady,
    contentReady,
    layoutInstant,
    placements,
    cardHeights,
    syncProseLayout,
    measureCardHeights,
    recomputePlacements,
    scheduleLayout,
    snapLayout,
    cleanup,
  };
}
